'use client';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';

import Audio from '@/decodo/components/atoms/Audio/Audio';
import Heading from '@/decodo/components/atoms/Heading';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import { Video } from '@/decodo/components/atoms/Video';
import PostByLine from '@/decodo/features/blog/PostByLine';
import { slugBlogAudioFileMap } from '@/utils/audio';

import PostShareMenu from './PostShareMenu';
import { PostTags, PostTopic } from './PostTags';

import IconChevronLeft from '~/svg/IconChevronLeft.svg';

export type BlogMetaDataProps = {
  image?: React.ReactNode;
  releaseDate: string;
  timeSpentReading?: number;
  isNew?: boolean;
  title?: string;
  video?: string;
  description?: React.ReactNode;
  topics?: PostTopic[];
  author?: {
    image?: React.ReactNode;
    name?: string;
  };
};

export default function BlogMetaData({
  image,
  releaseDate,
  timeSpentReading,
  title,
  video,
  description,
  topics,
  author,
  isNew,
}: BlogMetaDataProps) {
  const hasHeroImage = !!image;
  const hasHeroVideo = !!video;
  const hasHeroAsset = !!image || !!video;
  const hasPostTags = isNew || Number(topics?.length) > 0;
  const [postUrl, setPostUrl] = useState('');
  const { texts } = useTranslation();

  const blogAudioTrack = (() => {
    if (typeof window === 'undefined') return null;

    let slug = window.location.pathname.split('/').pop();
    if (slug?.includes('?')) slug = slug.split('?')[0];

    if (!slug) return null;

    const availableAudioFile =
      slugBlogAudioFileMap[slug as keyof typeof slugBlogAudioFileMap];

    if (!availableAudioFile) return null;

    return availableAudioFile;
  })();

  useEffect(() => {
    setPostUrl(window.location.href);
  }, []);

  return (
    <section
      className='w-full px-5 !pt-0 pb-8 md:px-10 md:pb-[40px]'
      data-tracker-section='blog-metadata'
      data-tracker-component='actions'
    >
      <LinkButton
        href='/blog'
        variant='text'
        color='secondary'
        leftSection={<IconChevronLeft />}
        underline={false}
        classNames={{
          root: 'text-gray-950 hover:text-base-black h-5 text-body-l leading-5 px-0',
        }}
      >
        {texts.blog.back_to_blog}
      </LinkButton>
      <div className='pt-12'>
        {hasPostTags && (
          <PostTags isNew={isNew ?? false} topics={topics ?? []} />
        )}
      </div>

      <div className='grid pt-3'>
        <Heading level='h1'>{title}</Heading>
        {blogAudioTrack && <Audio audioTrack={blogAudioTrack} />}
        {!!description && (
          <Text
            variant='subtitle-m'
            className='text-body-l sm:text-body-l pt-6'
          >
            {description}
          </Text>
        )}
      </div>
      <div>
        <div className='flex items-start justify-between pt-6	sm:items-center'>
          <PostByLine
            releaseDate={releaseDate}
            timeSpentReading={timeSpentReading}
            author={author}
          />
          <PostShareMenu title={title} url={postUrl} />
        </div>

        {hasHeroAsset && (
          <div className='border-b border-gray-200 pb-8 pt-8 sm:pb-[2.625rem] sm:pt-9'>
            {hasHeroImage && !hasHeroVideo && (
              <div className='w-full rounded'>{image} </div>
            )}
            {hasHeroVideo && (
              <Video classNames={{ container: 'rounded' }} url={video} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
